import React, { useState, useEffect } from 'react';
import './FilterModal.css';
import { IoMdCloseCircle } from "react-icons/io";

const FilterModal = ({ isOpen, onClose, onApplyFilters, onClearFilters, initialFilters }) => {
  const [isApplying, setIsApplying] = useState(false);
  const [filters, setFilters] = useState({
    selectedStatus: [],
    selectedServiceCategories: [],
    selectedPriorities: [],
    selectedClients: [],
    selectedEmployees: [],
    reportedFromDate: '',
    reportedToDate: '',
    scheduledFromDate: '',
    scheduledToDate: ''
  });

  const [expandedSections, setExpandedSections] = useState({
    status: false,
    serviceCategory: false,
    priority: false,
    reportedDate: false,
    scheduledDate: false
  });

  const statusList = [
    { label: 'REJECTED', value: 3 },
    { label: 'OPEN', value: 10 },
    { label: 'ASSIGNED', value: 12 },
    { label: 'WORK ON HOLD', value: 14 },
    { label: 'ACCEPTED', value: 16 },
    { label: 'WIP', value: [20, 21, 22, 23, 24, 25, 26, 27, 28, 30,32] },
    { label: 'COMPLETED', value: 34 }
  ];

  const serviceCategoryList = [
    { label: 'PAID', value: 0 },
    { label: 'UNDER WARRANTY', value: 1 },
    { label: 'AMC', value: 3 }
  ];

  const priorityList = [
    { label: 'LOW', value: 0 },
    { label: 'MEDIUM', value: 1 },
    { label: 'NORMAL', value: 2 },
    { label: 'HIGH', value: 3 },
    { label: 'URGENT', value: 4 }
  ];

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
      // Auto-expand sections with selected filters
      setExpandedSections({
        status: (initialFilters.selectedStatus?.length || 0) > 0,
        serviceCategory: (initialFilters.selectedServiceCategories?.length || 0) > 0,
        priority: (initialFilters.selectedPriorities?.length || 0) > 0,
        reportedDate: !!(initialFilters.reportedFromDate || initialFilters.reportedToDate),
        scheduledDate: !!(initialFilters.scheduledFromDate || initialFilters.scheduledToDate)
      });
    }
  }, [initialFilters, isOpen]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleStatusToggle = (statusValue) => {
    setFilters(prev => {
      const currentSelected = prev.selectedStatus || [];
      
      if (Array.isArray(statusValue)) {
        // Handle WIP status with multiple values
        const hasAnyWipStatus = statusValue.some(val => currentSelected.includes(val));
        if (hasAnyWipStatus) {
          // Remove all WIP statuses
          return {
            ...prev,
            selectedStatus: currentSelected.filter(s => !statusValue.includes(s))
          };
        } else {
          // Add all WIP statuses
          return {
            ...prev,
            selectedStatus: [...currentSelected, ...statusValue]
          };
        }
      } else {
        // Handle single status value
        return {
          ...prev,
          selectedStatus: currentSelected.includes(statusValue)
            ? currentSelected.filter(s => s !== statusValue)
            : [...currentSelected, statusValue]
        };
      }
    });
  };

  const handleServiceCategoryToggle = (categoryValue) => {
    setFilters(prev => ({
      ...prev,
      selectedServiceCategories: (prev.selectedServiceCategories || []).includes(categoryValue)
        ? (prev.selectedServiceCategories || []).filter(c => c !== categoryValue)
        : [...(prev.selectedServiceCategories || []), categoryValue]
    }));
  };

  const handlePriorityToggle = (priorityValue) => {
    setFilters(prev => ({
      ...prev,
      selectedPriorities: (prev.selectedPriorities || []).includes(priorityValue)
        ? (prev.selectedPriorities || []).filter(p => p !== priorityValue)
        : [...(prev.selectedPriorities || []), priorityValue]
    }));
  };

  const handleDateChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearAll = () => {
    setFilters({
      selectedStatus: [],
      selectedServiceCategories: [],
      selectedPriorities: [],
      selectedClients: [],
      selectedEmployees: [],
      reportedFromDate: '',
      reportedToDate: '',
      scheduledFromDate: '',
      scheduledToDate: ''
    });
    setExpandedSections({
      status: false,
      serviceCategory: false,
      priority: false,
      reportedDate: false,
      scheduledDate: false
    });
    onClearFilters();
    onClose();
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      await onApplyFilters(filters);
      onClose();
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setIsApplying(false);
    }
  };

  const hasActiveFilters = () => {
    return (filters.selectedStatus?.length > 0) ||
           (filters.selectedServiceCategories?.length > 0) ||
           (filters.selectedPriorities?.length > 0) ||
           (filters.selectedClients?.length > 0) ||
           (filters.selectedEmployees?.length > 0) ||
           filters.reportedFromDate ||
           filters.reportedToDate ||
           filters.scheduledFromDate ||
           filters.scheduledToDate;
  };

  if (!isOpen) return null;

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="filter-modal-header">
          <h2 className="filter-modal-title">Filters</h2>
          <div className="filter-modal-actions">
            <button 
              className="filter-clear-button"
              onClick={handleClearAll}
              disabled={!hasActiveFilters()}
            >
              Clear Filter
            </button>
            <button 
              className="filter-apply-button"
              onClick={handleApply}
              disabled={isApplying}
            >
              {isApplying ? 'Applying...' : 'Apply Filter'}
            </button>
          </div>
                  
          <IoMdCloseCircle  onClick={onClose} size={18} color='brown' className='drawer_close_icon'/>
        </div>

        {/* Content */}
        <div className="filter-modal-content">
          {/* Status Filter */}
          <div className="filter-accordion">
            <div 
              className="filter-accordion-header"
              onClick={() => toggleSection('status')}
            >
              <span>Your Request Status</span>
              <span className="filter-accordion-icon">
                {expandedSections.status ? '−' : '+'}
              </span>
            </div>
            {expandedSections.status && (
              <div className="filter-accordion-content">
                <div className="filter-chip-container">
                  {statusList.map((status) => {
                    const isActive = Array.isArray(status.value) 
                      ? status.value.some(val => (filters.selectedStatus || []).includes(val))
                      : (filters.selectedStatus || []).includes(status.value);
                    
                    return (
                      <button
                        key={Array.isArray(status.value) ? status.label : status.value}
                        className={`filter-chip ${isActive ? 'active' : ''}`}
                        onClick={() => handleStatusToggle(status.value)}
                      >
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Service Category Filter */}
          <div className="filter-accordion">
            <div 
              className="filter-accordion-header"
              onClick={() => toggleSection('serviceCategory')}
            >
              <span>Service Category</span>
              <span className="filter-accordion-icon">
                {expandedSections.serviceCategory ? '−' : '+'}
              </span>
            </div>
            {expandedSections.serviceCategory && (
              <div className="filter-accordion-content">
                <div className="filter-chip-container">
                  {serviceCategoryList.map((category) => (
                    <button
                      key={category.value}
                      className={`filter-chip ${(filters.selectedServiceCategories || []).includes(category.value) ? 'active' : ''}`}
                      onClick={() => handleServiceCategoryToggle(category.value)}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Priority Filter */}
          <div className="filter-accordion">
            <div 
              className="filter-accordion-header"
              onClick={() => toggleSection('priority')}
            >
              <span>Priority</span>
              <span className="filter-accordion-icon">
                {expandedSections.priority ? '−' : '+'}
              </span>
            </div>
            {expandedSections.priority && (
              <div className="filter-accordion-content">
                <div className="filter-chip-container">
                  {priorityList.map((priority) => (
                    <button
                      key={priority.value}
                      className={`filter-chip ${(filters.selectedPriorities || []).includes(priority.value) ? 'active' : ''}`}
                      onClick={() => handlePriorityToggle(priority.value)}
                    >
                      {priority.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Reported Date Filter */}
          <div className="filter-accordion">
            <div 
              className="filter-accordion-header"
              onClick={() => toggleSection('reportedDate')}
            >
              <span>Reported Date</span>
              <span className="filter-accordion-icon">
                {expandedSections.reportedDate ? '−' : '+'}
              </span>
            </div>
            {expandedSections.reportedDate && (
              <div className="filter-accordion-content">
                <div className="filter-date-row">
                  <div className="filter-date-field">
                    <label>From</label>
                    <input
                      type="date"
                      value={filters.reportedFromDate}
                      onChange={(e) => handleDateChange('reportedFromDate', e.target.value)}
                      className="filter-date-input"
                    />
                  </div>
                  <div className="filter-date-field">
                    <label>To</label>
                    <input
                      type="date"
                      value={filters.reportedToDate}
                      onChange={(e) => handleDateChange('reportedToDate', e.target.value)}
                      className="filter-date-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Scheduled Date Filter */}
          <div className="filter-accordion">
            <div 
              className="filter-accordion-header"
              onClick={() => toggleSection('scheduledDate')}
            >
              <span>Scheduled Date</span>
              <span className="filter-accordion-icon">
                {expandedSections.scheduledDate ? '−' : '+'}
              </span>
            </div>
            {expandedSections.scheduledDate && (
              <div className="filter-accordion-content">
                <div className="filter-date-row">
                  <div className="filter-date-field">
                    <label>From</label>
                    <input
                      type="date"
                      value={filters.scheduledFromDate}
                      onChange={(e) => handleDateChange('scheduledFromDate', e.target.value)}
                      className="filter-date-input"
                    />
                  </div>
                  <div className="filter-date-field">
                    <label>To</label>
                    <input
                      type="date"
                      value={filters.scheduledToDate}
                      onChange={(e) => handleDateChange('scheduledToDate', e.target.value)}
                      className="filter-date-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;